import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import Loading from '../../../components/Loading';
import CurrencyInput from '../../../components/MinorUtilities/CurrencyInput';
import ServiceAquisitionProviderPickerModal from '../ServiceAquisitionProviderPickerModal';
import { Container } from '../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
   PageHeaderActions,
} from '../../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../../styles/layout/Section';
import {
   ActionBar,
   ActionBarLeft,
   ActionBarRight,
} from '../../../styles/layout/ActionBar';
import { FormGrid } from '../../../styles/layout/FormGrid';
import { Field } from '../../../styles/forms/Field';
import axios from '../../../services/axios';

// ─── Estilos locais ───────────────────────────────────────────────────────────

const ProviderFieldWrapper = styled.div`
   display: flex;
   gap: 8px;
   align-items: flex-end;
`;

const ProviderDisplayInput = styled.input`
   flex: 1;
`;

const NfseDropZone = styled.div`
   border: 2px dashed
      ${({ $isDraggingOver, theme }) =>
         $isDraggingOver
            ? theme?.colors?.primary ?? '#1976d2'
            : theme?.colors?.border ?? 'rgba(0,0,0,0.2)'};
   border-radius: 8px;
   padding: 24px 16px;
   text-align: center;
   cursor: pointer;
   transition:
      border-color 0.2s,
      background 0.2s;
   background: ${({ $isDraggingOver, theme }) =>
      $isDraggingOver
         ? theme?.colors?.primarySoft ?? 'rgba(25,118,210,0.06)'
         : 'transparent'};

   p {
      margin: 4px 0;
      font-size: 13px;
      color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
   }
   strong {
      font-size: 14px;
      color: ${({ theme }) => theme?.colors?.textPrimary ?? '#222'};
   }
`;

const NfseFilePreview = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   padding: 10px 14px;
   border-radius: 8px;
   border: 1px solid
      ${({ theme }) => theme?.colors?.border ?? 'rgba(0,0,0,0.12)'};
   font-size: 13px;

   span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
   }
   button {
      flex-shrink: 0;
      font-size: 12px;
      padding: 4px 10px;
   }
`;

// ─── Helper: converte YYYY-MM (input type=month) para MM/AAAA (backend) ───────
function convertMonthInputToReferencePeriod(yearMonthString) {
   if (!yearMonthString) return '';
   const [year, month] = yearMonthString.split('-');
   return `${month}/${year}`;
}

// ─── Helper: converte MM/AAAA (backend) para YYYY-MM (input type=month) ───────
function convertReferencePeriodToMonthInput(referencePeriod) {
   if (!referencePeriod) return '';
   const [month, year] = referencePeriod.split('/');
   return `${year}-${month}`;
}

// ─── Helper: extrai valor numérico da string formatada do CurrencyInput ────────
// CurrencyInput retorna "R$ 1.250,00" — converte para 1250.00
function extractNumericValueFromCurrencyString(currencyString) {
   if (!currencyString) return 0;
   const numericString = String(currencyString)
      .replace(/[R$\s.]/g, '') // remove R$, espaços e pontos de milhar
      .replace(',', '.'); // troca vírgula decimal por ponto
   return parseFloat(numericString) || 0;
}

// ─── Componente de upload com drag and drop ───────────────────────────────────
function NfseUploadField({
   selectedFile,
   existingFileUrl,
   onFileSelect,
   onFileRemove,
}) {
   const [isDraggingOver, setIsDraggingOver] = useState(false);
   const fileInputRef = useRef(null);

   function handleDragOver(event) {
      event.preventDefault();
      setIsDraggingOver(true);
   }
   function handleDragLeave() {
      setIsDraggingOver(false);
   }

   function handleDrop(event) {
      event.preventDefault();
      setIsDraggingOver(false);
      const droppedFile = event.dataTransfer.files[0];
      if (droppedFile) onFileSelect(droppedFile);
   }

   function handleFileInputChange(event) {
      const chosenFile = event.target.files[0];
      if (chosenFile) onFileSelect(chosenFile);
      event.target.value = '';
   }

   if (selectedFile) {
      return (
         <NfseFilePreview>
            <span title={selectedFile.name}>📄 {selectedFile.name}</span>
            <button type="button" onClick={onFileRemove}>
               Remover
            </button>
         </NfseFilePreview>
      );
   }

   if (existingFileUrl) {
      return (
         <NfseFilePreview>
            <span>
               Arquivo existente —&nbsp;
               <a
                  href={existingFileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
               >
                  visualizar
               </a>
            </span>
            <button type="button" onClick={() => fileInputRef.current?.click()}>
               Substituir
            </button>
            <input
               ref={fileInputRef}
               type="file"
               accept="application/pdf"
               style={{ display: 'none' }}
               onChange={handleFileInputChange}
            />
         </NfseFilePreview>
      );
   }

   return (
      <>
         <NfseDropZone
            $isDraggingOver={isDraggingOver}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) =>
               e.key === 'Enter' && fileInputRef.current?.click()
            }
         >
            <strong>Arraste o PDF da NFS-e aqui</strong>
            <p>ou clique para selecionar o arquivo</p>
            <p>Somente PDF — máximo 20 MB</p>
         </NfseDropZone>
         <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            style={{ display: 'none' }}
            onChange={handleFileInputChange}
         />
      </>
   );
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function ServiceAquisitionForm() {
   const { id } = useParams();
   const navigate = useNavigate();

   const isEditMode = Boolean(id);

   // ─── Campos do formulário ────────────────────────────────────────────────
   const [legalEntity, setLegalEntity] = useState('');
   const [selectedProvider, setSelectedProvider] = useState(null);
   const [serviceCategory, setServiceCategory] = useState('');
   const [nfseNumber, setNfseNumber] = useState('');
   // totalAmountFormatted armazena a string do CurrencyInput ex: "R$ 1.250,00"
   const [totalAmountFormatted, setTotalAmountFormatted] = useState('R$ 0,00');
   // referencePeriodMonthInput armazena no formato YYYY-MM para o input type=month
   const [referencePeriodMonthInput, setReferencePeriodMonthInput] =
      useState('');
   const [documentGenerationDate, setDocumentGenerationDate] = useState('');
   const [serviceDescription, setServiceDescription] = useState('');

   // ─── Arquivo NFS-e ───────────────────────────────────────────────────────
   const [selectedNfseFile, setSelectedNfseFile] = useState(null);
   const [existingNfseFileUrl, setExistingNfseFileUrl] = useState(null);

   // ─── Estado geral ────────────────────────────────────────────────────────
   const [isLoading, setIsLoading] = useState(false);
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [isProviderModalOpen, setIsProviderModalOpen] = useState(false);

   // ─── Carrega dados no modo edição ────────────────────────────────────────
   const fetchServiceAquisitionForEdit = useCallback(async () => {
      setIsLoading(true);
      try {
         const response = await axios.get(`/service-aquisition/${id}`);
         const existingRecord = response.data.data.serviceAquisition;
         const presignedUrl = existingRecord.nfse.presigned_download_url;

         setLegalEntity(existingRecord.legal_entity ?? '');
         setSelectedProvider(existingRecord.provider ?? null);
         setServiceCategory(existingRecord.service_category ?? '');
         setNfseNumber(existingRecord.nfse.number ?? '');
         // Converte o valor numérico para string formatada do CurrencyInput
         setTotalAmountFormatted(
            existingRecord.total_amount
               ? String(existingRecord.total_amount)
                    .replace('.', ',')
                    .padStart(4, '0')
               : 'R$ 0,00'
         );
         // Converte MM/AAAA para YYYY-MM para o input type=month
         setReferencePeriodMonthInput(
            convertReferencePeriodToMonthInput(existingRecord.reference_period)
         );
         setDocumentGenerationDate(
            existingRecord.document_generation_date ?? ''
         );
         setServiceDescription(existingRecord.service_description ?? '');
         setExistingNfseFileUrl(
            existingRecord.nfse.has_file ? presignedUrl : null
         );
      } catch (err) {
         const errorMessage =
            err.response?.data?.errors?.[0]?.message ??
            err.response?.data?.message ??
            'Erro ao carregar registro';
         toast.error(errorMessage);
         navigate('/finance/service-aquisition');
      } finally {
         setIsLoading(false);
      }
   }, [id, navigate]);

   useEffect(() => {
      if (isEditMode) fetchServiceAquisitionForEdit();
   }, [isEditMode, fetchServiceAquisitionForEdit]);

   // ─── Submit ──────────────────────────────────────────────────────────────
   async function handleSubmit(event) {
      event.preventDefault();

      if (!selectedProvider) {
         toast.error('Selecione um prestador de serviço');
         return;
      }

      if (!referencePeriodMonthInput) {
         toast.error('Período de referência é obrigatório');
         return;
      }

      const numericTotalAmount =
         extractNumericValueFromCurrencyString(totalAmountFormatted);
      if (numericTotalAmount <= 0) {
         toast.error('Valor total deve ser maior que zero');
         return;
      }

      setIsSubmitting(true);
      try {
         const formData = new FormData();
         formData.append('legal_entity', legalEntity);
         formData.append('service_aquisition_provider_id', selectedProvider.id);
         formData.append('service_category', serviceCategory);
         formData.append('total_amount', numericTotalAmount);
         // Converte YYYY-MM para MM/AAAA antes de enviar ao backend
         formData.append(
            'reference_period',
            convertMonthInputToReferencePeriod(referencePeriodMonthInput)
         );

         if (nfseNumber) formData.append('nfse_number', nfseNumber);
         if (documentGenerationDate)
            formData.append('document_generation_date', documentGenerationDate);
         if (serviceDescription)
            formData.append('service_description', serviceDescription);
         if (selectedNfseFile) formData.append('nfse_file', selectedNfseFile);

         if (isEditMode) {
            await axios.put(`/service-aquisition/${id}`, formData, {
               headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.success('Registro atualizado com sucesso');
            navigate(`/finance/service-aquisition/${id}`);
         } else {
            const response = await axios.post('/service-aquisition', formData, {
               headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.success('Serviço tomado registrado com sucesso');
            navigate(
               `/finance/service-aquisition/${response.data.data.serviceAquisition.id}`
            );
         }
      } catch (err) {
         const validationErrors = err.response?.data?.errors;
         if (Array.isArray(validationErrors)) {
            validationErrors.forEach((validationError) => {
               toast.error(
                  `${validationError.field}: ${validationError.message}`
               );
            });
            return;
         }
         const errorMessage =
            err.response?.data?.message ?? 'Erro ao salvar registro';
         toast.error(errorMessage);
      } finally {
         setIsSubmitting(false);
      }
   }

   return (
      <Container>
         <Loading isLoading={isLoading || isSubmitting} />

         <ServiceAquisitionProviderPickerModal
            isOpen={isProviderModalOpen}
            onSelect={(provider) => setSelectedProvider(provider)}
            onClose={() => setIsProviderModalOpen(false)}
         />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>
                  {isEditMode ? 'Editar Serviço Tomado' : 'Novo Serviço Tomado'}
               </PageTitle>
               <PageSubtitle>
                  {isEditMode
                     ? 'Atualize os dados do registro abaixo'
                     : 'Preencha os dados do serviço tomado'}
               </PageSubtitle>
            </PageHeaderText>
            <PageHeaderActions>
               <button
                  type="button"
                  onClick={() =>
                     navigate(
                        isEditMode
                           ? `/finance/service-aquisition/${id}`
                           : '/finance/service-aquisition'
                     )
                  }
                  disabled={isSubmitting}
               >
                  Cancelar
               </button>
            </PageHeaderActions>
         </PageHeader>

         <form onSubmit={handleSubmit}>
            <Section>
               <SectionHeader>
                  <SectionTitle>Dados do Serviço</SectionTitle>
               </SectionHeader>
               <SectionBody>
                  <FormGrid>
                     <Field className="col-4">
                        <label htmlFor="legal-entity">
                           Entidade Jurídica *
                        </label>
                        <select
                           id="legal-entity"
                           value={legalEntity}
                           onChange={(e) => setLegalEntity(e.target.value)}
                           required
                           disabled={isSubmitting}
                        >
                           <option value="">Selecione...</option>
                           <option value="MYSHOP_COMERCIO">
                              MyShop Comércio
                           </option>
                           <option value="LIFE_PURIFICADORES">
                              Life Purificadores
                           </option>
                        </select>
                     </Field>

                     <Field className="col-4">
                        <label htmlFor="service-category">Categoria *</label>
                        <select
                           id="service-category"
                           value={serviceCategory}
                           onChange={(e) => setServiceCategory(e.target.value)}
                           required
                           disabled={isSubmitting}
                        >
                           <option value="">Selecione...</option>
                           <option value="SOFTWARE">Software</option>
                           <option value="ECOMMERCE">E-commerce</option>
                           <option value="SERVICO_FISICO">
                              Serviço Físico
                           </option>
                           <option value="CONSULTORIA">Consultoria</option>
                           <option value="MARKETING_DIGITAL">
                              Marketing Digital
                           </option>
                           <option value="OUTROS">Outros</option>
                        </select>
                     </Field>

                     <Field className="col-4">
                        <label>Prestador de Serviço *</label>
                        <ProviderFieldWrapper>
                           <ProviderDisplayInput
                              type="text"
                              value={
                                 selectedProvider
                                    ? selectedProvider.provider_name
                                    : ''
                              }
                              placeholder="Nenhum prestador selecionado"
                              disabled
                              readOnly
                           />
                           <button
                              type="button"
                              onClick={() => setIsProviderModalOpen(true)}
                              disabled={isSubmitting}
                              style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                           >
                              Pesquisar
                           </button>
                        </ProviderFieldWrapper>
                        {selectedProvider?.cnpj_number && (
                           <p
                              style={{
                                 fontSize: 11,
                                 color: '#888',
                                 margin: '4px 0 0',
                              }}
                           >
                              CNPJ: {selectedProvider.cnpj_number}
                           </p>
                        )}
                     </Field>

                     {/* ─── Valor Total via CurrencyInput ─────────────────────── */}
                     <Field className="col-3">
                        <label htmlFor="total-amount">Valor Total (R$) *</label>
                        <CurrencyInput
                           value={totalAmountFormatted}
                           onChange={(formattedValue) =>
                              setTotalAmountFormatted(formattedValue)
                           }
                           disabled={isSubmitting}
                        />
                     </Field>

                     {/* ─── Período de referência via input type=month ────────── */}
                     <Field className="col-3">
                        <label htmlFor="reference-period">
                           Período de Referência *
                        </label>
                        <input
                           id="reference-period"
                           type="month"
                           value={referencePeriodMonthInput}
                           onChange={(e) =>
                              setReferencePeriodMonthInput(e.target.value)
                           }
                           required
                           disabled={isSubmitting}
                        />
                     </Field>

                     <Field className="col-3">
                        <label htmlFor="document-generation-date">
                           Data de Emissão
                        </label>
                        <input
                           id="document-generation-date"
                           type="date"
                           value={documentGenerationDate}
                           onChange={(e) =>
                              setDocumentGenerationDate(e.target.value)
                           }
                           disabled={isSubmitting}
                        />
                     </Field>

                     <Field className="col-3">
                        <label htmlFor="nfse-number">Número da NFS-e</label>
                        <input
                           id="nfse-number"
                           type="text"
                           placeholder="Número da nota"
                           maxLength={50}
                           value={nfseNumber}
                           onChange={(e) => setNfseNumber(e.target.value)}
                           disabled={isSubmitting}
                        />
                     </Field>

                     <Field className="col-12">
                        <label htmlFor="service-description">
                           Descrição do Serviço
                        </label>
                        <textarea
                           id="service-description"
                           rows={4}
                           placeholder="Descreva o serviço tomado..."
                           value={serviceDescription}
                           onChange={(e) =>
                              setServiceDescription(e.target.value)
                           }
                           disabled={isSubmitting}
                        />
                     </Field>
                  </FormGrid>
               </SectionBody>
            </Section>

            <Section>
               <SectionHeader>
                  <SectionTitle>Arquivo NFS-e (PDF)</SectionTitle>
               </SectionHeader>
               <SectionBody>
                  <NfseUploadField
                     selectedFile={selectedNfseFile}
                     existingFileUrl={existingNfseFileUrl}
                     onFileSelect={(file) => setSelectedNfseFile(file)}
                     onFileRemove={() => setSelectedNfseFile(null)}
                  />
               </SectionBody>
            </Section>

            <ActionBar>
               <ActionBarLeft />
               <ActionBarRight>
                  <button
                     type="button"
                     onClick={() =>
                        navigate(
                           isEditMode
                              ? `/finance/service-aquisition/${id}`
                              : '/finance/service-aquisition'
                        )
                     }
                     disabled={isSubmitting}
                  >
                     Cancelar
                  </button>
                  <button type="submit" disabled={isSubmitting}>
                     {isSubmitting
                        ? 'Salvando...'
                        : isEditMode
                          ? 'Salvar Alterações'
                          : 'Registrar Serviço'}
                  </button>
               </ActionBarRight>
            </ActionBar>
         </form>
      </Container>
   );
}
